import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import Button from '~/components/buttons/NewButton';
import CountryController from '~/components/controllers/CountryController';
import FiscalRegimeController from '~/components/controllers/FiscalRegimeController';
import TextController from '~/components/controllers/TextController/TextController';
import ModalActions from '~/components/modals/Modal/ModalActions';
import { FiscalRegime } from '~/types/company';
import { countryIsoCodes } from '~/types/country';
import { isItalianTaxNumber, isItalianVatNumber } from '~/utils/validation';
import styles from './AddCompanyForm.module.scss';

export const useCompanyDataSchema = () => {
  const { t } = useTranslation('validations');
  return useMemo(
    () =>
      z.object({
        name: z.string().min(1),
        country: z.enum(countryIsoCodes),
        vatNumber: z
          .string()
          .min(1)
          .length(11, t('exact_string', { count: 11 }))
          .refine(isItalianVatNumber, t('invalid_vat_number')),
        taxNumber: z
          .string()
          .min(1)
          .superRefine((v, ctx) => {
            if (isItalianVatNumber(v) || isItalianTaxNumber(v)) {
              return;
            }

            if (new RegExp(/\d+/).test(v)) {
              return ctx.addIssue({
                message: t('exact_string', { count: 11 }),
                code: z.ZodIssueCode.custom,
              });
            }

            return ctx.addIssue({
              message: t('exact_string', { count: 16 }),
              code: z.ZodIssueCode.custom,
            });
          }),
        fiscalRegime: z.nativeEnum(FiscalRegime),
        address: z.string().min(1),
        city: z.string().min(1),
        postalCode: z.string().min(1),
      }),
    [t],
  );
};

export type CompanyData = z.infer<ReturnType<typeof useCompanyDataSchema>>;

const AddCompanyForm: React.FC<{
  initialValues?: Partial<CompanyData>;
  vatNumberDisabled?: boolean;
  onCancel: () => void;
  onSubmit: (data: CompanyData) => void;
}> = ({ initialValues, vatNumberDisabled, onCancel, onSubmit }) => {
  const { t } = useTranslation('auth', { keyPrefix: 'signup.add_company.form' });
  const { t: tt } = useTranslation('translations');

  const companyDataSchema = useCompanyDataSchema();

  const form = useForm<CompanyData>({
    defaultValues: {
      name: '',
      country: 'IT',
      vatNumber: '',
      taxNumber: '',
      fiscalRegime: FiscalRegime.RF01,
      address: '',
      city: '',
      postalCode: '',
      ...initialValues,
    },
    resolver: zodResolver(companyDataSchema),
  });

  return (
    <form className={styles.form} noValidate>
      <TextController className={styles.fullWidth} control={form.control} name="name" label={t('name')} required />

      <div className={styles.formRow}>
        <CountryController
          control={form.control}
          name="country"
          label={t('country')}
          required
          className={styles.fullWidth}
        />
        <TextController
          control={form.control}
          name="vatNumber"
          label={t('vat_number')}
          required
          className={styles.fullWidth}
          disabled={vatNumberDisabled}
        />
      </div>

      <div className={styles.formRow}>
        <TextController
          className={styles.fullWidth}
          control={form.control}
          name="taxNumber"
          label={t('tax_number')}
          required
          disabled
        />
        <FiscalRegimeController
          className={styles.fullWidth}
          control={form.control}
          name="fiscalRegime"
          label={t('fiscal_regime')}
          required
        />
      </div>

      <TextController
        className={styles.fullWidth}
        control={form.control}
        name="address"
        label={t('address')}
        required
      />

      <div className={styles.formRow}>
        <TextController className={styles.fullWidth} control={form.control} name="city" label={t('city')} required />
        <TextController
          className={styles.fullWidth}
          control={form.control}
          name="postalCode"
          label={t('postal_code')}
          required
        />
      </div>

      <ModalActions className={styles.modalAction}>
        <Button onClick={() => onCancel()} variant="outlined">
          {tt('BACK')}
        </Button>
        <Button onClick={() => form.handleSubmit((v) => onSubmit(v))()}>{tt('CONFIRM')}</Button>
      </ModalActions>
    </form>
  );
};

export default AddCompanyForm;
